import React, { PureComponent } from 'react';
import Select, { Creatable, Value } from 'react-select';
import VirtualizedSelect from 'react-virtualized-select';
import classnames from 'classnames';
import uniq from 'lodash.uniq';
import { findDOMNode } from 'react-dom';
import throttle from 'lodash.throttle';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

const filterSelectFactory = function () {
  let valueKey = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'label';
  let removeSelected = arguments.length > 1 ? arguments[1] : undefined;
  return (options, filter, currentOptions) => {
    const optionsFilteredBySelection = removeSelected ? options.filter(option => !currentOptions || !currentOptions.find(currentOption => currentOption.value === option.value)) : options;
    if (!filter) {
      return optionsFilteredBySelection;
    }
    return optionsFilteredBySelection.filter(option => {
      const value = option[valueKey];
      if (typeof value === 'string') {
        return value.toLowerCase().indexOf(filter.toLowerCase()) > -1;
      }
      return value.toString().toLowerCase().indexOf(filter.toLowerCase()) > -1;
    });
  };
};

const VirtualizedOption = _ref => {
  let {
    focusedOption,
    focusOption,
    key,
    labelKey,
    descriptionKey = 'description',
    option,
    selectValue,
    style,
    valueArray
  } = _ref;
  const events = option.disabled ? {} : {
    onClick: () => selectValue(option),
    onMouseEnter: () => focusOption(option)
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classnames('VirtualizedSelectOption', option.className, {
      VirtualizedSelectFocusedOption: option === focusedOption,
      VirtualizedSelectDisabledOption: option.disabled,
      VirtualizedSelectSelectedOption: valueArray && valueArray.indexOf(option) >= 0,
      VirtualizedSelectOptionWithDesc: Boolean(option[descriptionKey])
    }),
    key: key,
    style: style,
    title: option[labelKey]
  }, events), /*#__PURE__*/React.createElement("div", {
    className: "VirtualizedSelectOption-label"
  }, option[labelKey]), option[descriptionKey] && /*#__PURE__*/React.createElement("div", {
    className: "VirtualizedSelectOption-description"
  }, option[descriptionKey]));
};

const allOption = {
  value: 'ALL',
  label: 'All'
};
const defaultReactSelectOptionHeight = 35;
const defaultReactSelectOptionWithDescHeight = 55;
class CustomSelect extends PureComponent {
  constructor(props) {
    super(props);
    this.autoSelect = () => {
      const {
        onChange,
        options
      } = this.props;
      if (onChange) {
        onChange(options[0]);
      }
    };
    this.autoSelectAll = () => {
      const {
        onChange,
        options
      } = this.props;
      if (onChange) {
        onChange(options);
      }
    };
    this.optionHeight = _ref => {
      let {
        option
      } = _ref;
      const {
        descriptionKey
      } = this.props;
      return option[descriptionKey] ? defaultReactSelectOptionWithDescHeight : defaultReactSelectOptionHeight;
    };
    this.onChange = selectedItems => {
      const {
        onChange,
        options,
        withAll
      } = this.props;
      if (onChange) {
        if (withAll && selectedItems.includes(allOption)) {
          return onChange(options);
        }
        return onChange(selectedItems);
      }
    };
    this.state = {
      filterOption: filterSelectFactory(props.labelKey, props.removeSelected)
    };
  }
  componentDidMount() {
    const {
      autoSelectFirstValue,
      autoSelectAllValues,
      options,
      value
    } = this.props;
    if (autoSelectFirstValue && options.length && !value) {
      this.autoSelect();
    }
    if (autoSelectAllValues && options.length && (!value || !value.length)) {
      this.autoSelectAll();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      options,
      valueKey,
      autoSelectFirstValue,
      autoSelectAllValues,
      value
    } = this.props;
    if (autoSelectFirstValue || autoSelectAllValues) {
      const previousOptions = prevProps.options[0] ? prevProps.options.map(option => option[valueKey]) : [];
      if (autoSelectFirstValue && options.length && !previousOptions.length && !value) {
        this.autoSelect();
      }
      if (autoSelectAllValues && options.length && !previousOptions.length && (!value || !value.length)) {
        this.autoSelectAll();
      }
    }
  }
  render() {
    const {
      virtualized,
      options,
      withAll,
      ...rest
    } = this.props;
    const {
      filterOption
    } = this.state;
    const allOptions = withAll ? [allOption, ...options] : options;
    const SelectComponent = virtualized ? VirtualizedSelect : Select;
    const optionRenderer = virtualized ? VirtualizedOption : null;
    return /*#__PURE__*/React.createElement(SelectComponent, _extends({}, rest, {
      options: allOptions,
      onChange: this.onChange,
      optionHeight: this.optionHeight,
      filterOptions: filterOption,
      optionRenderer: optionRenderer
    }));
  }
}
CustomSelect.defaultProps = {
  virtualized: false,
  withAll: false,
  labelKey: 'label',
  valueKey: 'value',
  descriptionKey: 'description',
  autoSelectFirstValue: false,
  autoSelectAllValues: false,
  removeSelected: false
};

const COMMA_DELIMITED_VALUES_REGEX = /[\s,]+/;

// source https://github.com/JedWatson/react-select/issues/1672#issuecomment-304385354
class PasteCreatable extends PureComponent {
  render() {
    return /*#__PURE__*/React.createElement(Creatable, _extends({}, this.props, {
      ref: ref => this.pasteInput = ref,
      inputProps: {
        onPaste: event => {
          event.preventDefault();
          const clipboard = event.clipboardData.getData('Text');
          if (!clipboard) {
            return;
          }
          const values = uniq(clipboard.split(COMMA_DELIMITED_VALUES_REGEX), value => value.trim());
          const options = values.filter(value => this.pasteInput.props.isValidNewOption({
            label: value
          })).map(value => ({
            [this.pasteInput.labelKey]: value,
            [this.pasteInput.valueKey]: value,
            className: 'Select-create-option-placeholder'
          }));
          this.pasteInput.select.selectValue(options);
        }
      }
    }));
  }
}

class TruncatableSelect extends PureComponent {
  constructor(_props) {
    super(_props);
    this.state = {
      hideValues: true,
      collapsed: true
    };
    this.resizeHandler = () => {
      this.setState({
        hideValues: true
      });
      this.checkHowManyOptionsToDisplay();
    };
    this.checkHowManyOptionsToDisplay = () => {
      const values = findDOMNode(this.selectRef).querySelectorAll('.Select-value'); // eslint-disable-line react/no-find-dom-node

      const {
        index
      } = Array.from(values).reduce((acc, curr, index) => {
        if (acc.hasFound) {
          return acc;
        }
        return curr.offsetLeft > acc.offset ? {
          ...acc,
          offset: curr.offsetLeft,
          index
        } : {
          ...acc,
          hasFound: true
        };
      }, {
        hasFound: false,
        offset: 0,
        index: -1
      });
      this.setState({
        numberOfValuesToDisplay: index + 1,
        hideValues: false
      });
    };
    this.toggle = () => {
      const {
        onToggle
      } = this.props;
      this.setState(prevState => ({
        collapsed: !prevState.collapsed
      }), () => {
        if (onToggle) {
          onToggle(this.state.collapsed);
        }
      });
    };
    this.onChange = elements => {
      const {
        onChange
      } = this.props;
      if (!elements.length) {
        this.setState({
          collapsed: true
        });
      }
      if (onChange) {
        onChange(elements);
      }
    };
    this.renderValue = props => {
      const {
        numberOfValuesToDisplay,
        hideValues
      } = this.state;
      const index = this.props.value.findIndex(value => value === props.value);
      if (hideValues || index < numberOfValuesToDisplay) {
        return /*#__PURE__*/React.createElement(Value, props);
      }
      return null;
    };
    this.throttledResizeHandler = throttle(this.resizeHandler, 100);
  }
  componentDidMount() {
    this.checkHowManyOptionsToDisplay();
    window.addEventListener('resize', this.throttledResizeHandler);
  }
  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.setState({
        hideValues: true
      });
    }
    if (this.state.hideValues) {
      this.checkHowManyOptionsToDisplay();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledResizeHandler);
  }
  render() {
    const {
      selectComponentClassName,
      className,
      ...props
    } = this.props;
    const {
      hideValues,
      collapsed,
      numberOfValuesToDisplay
    } = this.state;
    const numberHiddenValues = props.value.length - numberOfValuesToDisplay;
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(className, 'select--truncatable__wrapper')
    }, /*#__PURE__*/React.createElement(CustomSelect, _extends({
      className: classnames(selectComponentClassName, 'select--truncatable', {
        'select--hidden-values': hideValues,
        'select--collapsed': collapsed
      }),
      ref: component => this.selectRef = component,
      valueComponent: collapsed ? this.renderValue : Value
    }, props, {
      onChange: this.onChange
    })), !numberHiddenValues ? null : /*#__PURE__*/React.createElement("button", {
      className: "select--truncatable__link link-button",
      onClick: this.toggle,
      tabIndex: "0"
    }, collapsed ? `show ${numberHiddenValues} more` : `show ${numberHiddenValues} less`));
  }
}

export { PasteCreatable, TruncatableSelect, CustomSelect as default };
